import { PageHeader } from "@/components/PageHeader";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { StatsBand } from "@/components/sections/StatsBand";
import { CtaBanner } from "@/components/sections/CtaBanner";
import { IMAGES } from "@/data/content";
import { HeartHandshake, ShieldCheck, Clock, Users } from "lucide-react";

const values = [
  { icon: Clock, title: "Fast response", body: "Average answer time under 15 seconds, 24 hours a day, 365 days a year." },
  { icon: HeartHandshake, title: "Compassionate care", body: "Real US-based specialists who listen — never scripts, never pressure." },
  { icon: ShieldCheck, title: "Always there", body: "UL-Listed, TMA Five Diamond Certified monitoring you can count on." },
  { icon: Users, title: "Family first", body: "Caregiver tools that keep the whole family connected and at ease." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About Us"
        title={<>We are here <span className="text-brand-red">to help.</span></>}
        sub="LifeButton.org exists for one reason: so that a single press of a button is all it takes to bring help, comfort, and peace of mind."
      />

      <section className="py-20 lg:py-28 bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative">
              <img
                src={IMAGES.couple}
                alt="A protected senior couple enjoying time together"
                className="rounded-[2rem] shadow-cardHover w-full h-[460px] object-cover ring-1 ring-brand-line"
              />
              <div className="absolute -bottom-6 -right-6 bg-brand-red text-white rounded-2xl p-6 shadow-red hidden sm:block">
                <p className="font-display font-black text-4xl leading-none">12,400+</p>
                <p className="text-white/90 font-semibold mt-1">families protected</p>
              </div>
            </div>
          </Reveal>

          <div>
            <SectionHeading
              eyebrow="Our Mission"
              title={<>Independence should never mean being <span className="text-brand-red">alone.</span></>}
            />
            <Reveal delay={0.1}>
              <div className="mt-6 space-y-5 text-lg text-brand-ink2 leading-relaxed">
                <p>
                  As the sister company to LifeAlarm Direct, LifeButton.org was founded on a simple
                  belief: aging at home should be a choice, not a risk. Every device we ship is
                  backed by a monitoring center staffed entirely by US-based specialists.
                </p>
                <p>
                  We don't lock families into multi-year contracts or bury fees in the fine print.
                  We earn your trust one month at a time — with fast response, honest advice, and
                  technology that fades into the background so life can move to the front.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <StatsBand />

      <section className="py-20 lg:py-28 bg-brand-bg">
        <div className="container-x">
          <SectionHeading
            align="center"
            eyebrow="What We Stand For"
            title={<>Fast response. Compassionate care. <span className="text-brand-red">Always there.</span></>}
          />
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" gap={0.1}>
            {values.map((v) => (
              <StaggerItem key={v.title}>
                <div className="h-full rounded-3xl bg-white border border-brand-line p-8 shadow-card">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl bg-brand-navy">
                    <v.icon className="w-7 h-7 text-brand-red" strokeWidth={2.2} />
                  </span>
                  <h3 className="mt-6 font-display font-bold text-xl text-brand-navy">{v.title}</h3>
                  <p className="mt-3 text-brand-ink2 leading-relaxed">{v.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Brand mark */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container-x">
          <Reveal>
            <div className="rounded-[2rem] border border-brand-line bg-brand-bg p-8 sm:p-12 text-center">
              <span className="eyebrow justify-center">
                <span className="inline-block h-px w-7 bg-brand-red" />
                Our Brand
              </span>
              <img
                src={IMAGES.logoLight}
                alt="LifeButton.org — We Are Here To Help"
                className="mx-auto mt-6 w-full max-w-2xl rounded-xl"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
